import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { BOARD_FILTER_KEYS, BoardFilterSelects } from '../workflow/EngagementProgressBoard';
import EngagementStatusFilter from './EngagementStatusFilter';
import { fetchJSON } from '../../utils/api';

// Management 清單頁共用的 engagement 篩選列：Status 晶片＋集團／FYE 年份／
// 合夥人／服務（Engagement Status、QC6/QC7、Signed FS、QC35 Records 都用這一份）。
// 四個下拉的條件放網址參數；後端條件在 server/utils/engagementFilters.js。
//
//   optionsPath      — 回 { statuses, defaultStatuses, filterOptions } 的端點
//   statusDefault    — 'lookup'＝預設勾 Dropdown Lists 的 show_by_default；
//                      'none'＝預設不勾（全部列出）
//
// 回傳 { ready, params, statusOptions, statuses, statusChips, selects }：params 併進 extraParams，
// statusChips 放 toolbarTop，selects 放 filters。ready 為 false 時不開表，
// 免得先閃一次未篩選的全部。
const useEngagementListFilters = ({ optionsPath, statusDefault = 'lookup' }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [statusOptions, setStatusOptions] = useState([]);
  const [filterOptions, setFilterOptions] = useState(null);
  const [statuses, setStatuses] = useState(null);

  const filterValues = Object.fromEntries(BOARD_FILTER_KEYS.map((k) => [k, searchParams.get(k) || '']));
  const { group, fy, partner, service } = filterValues;
  const setFilters = (patch) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(patch).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
      return next;
    }, { replace: true });
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchJSON(optionsPath);
        if (cancelled) return;
        setStatusOptions(data.statuses || []);
        setFilterOptions(data.filterOptions || null);
        setStatuses(statusDefault === 'lookup' ? data.defaultStatuses || [] : []);
      } catch {
        if (!cancelled) setStatuses([]);   // 拿不到選項就不篩，表照樣看得到
      }
    })();
    return () => { cancelled = true; };
  }, [optionsPath, statusDefault]);

  const toggleStatus = (value) => setStatuses((prev) => (
    (prev || []).includes(value) ? prev.filter((s) => s !== value) : [...(prev || []), value]
  ));

  const statusKey = (statuses || []).join(',');
  const params = useMemo(() => ({
    ...(statusKey ? { statuses: statusKey } : {}),
    ...(group ? { group } : {}),
    ...(fy ? { fy } : {}),
    ...(partner ? { partner } : {}),
    ...(service ? { service } : {}),
  }), [statusKey, group, fy, partner, service]);

  return {
    ready: statuses !== null,
    params,
    statusOptions,
    statuses: statuses || [],
    statusChips: <EngagementStatusFilter options={statusOptions} active={statuses || []} onToggle={toggleStatus} />,
    selects: <BoardFilterSelects values={filterValues} setFilters={setFilters} filterOptions={filterOptions} />,
  };
};

export default useEngagementListFilters;
